import { reactive, ref } from "vue";
import { currentService } from "@/views/project-list";
import mime from "mime";
import {
    UPLOAD_MAX_ATTEMPTS,
    isRetriableUploadError,
    uploadRetryDelayMs,
    sleep,
    persistedUploadId,
    persistPendingUploads,
    unpersistPendingUpload,
    updatePersistedUpload,
    requestPersistentStorage,
    rehydratePersistedFile,
    acquireUploadKeepalive,
    releaseUploadKeepalive,
    beginIndexSetup,
    clearUntrackedUploads,
    PERSIST_MAX_FILE_BYTES,
    type PersistedUpload,
} from "./upload_durability";

export let fileToUpload = ref();
export let currentDirectory = ref("");
export let uploadGroups: FormData[] = [];
export let serviceFolders: any = {}; // {serviceid: {dirname: {}}}
export let dbServiceFolders: any = {}; // {serviceid: {dirname: {}}}
export let urlCache: { [path: string]: { url: string; expiresAt: number } } = {};

// Persistent AI-index cache for dbfile.vue, keyed by service id → set of storage
// paths CONFIRMED indexed (their `src::<storagePath>` record exists). Module-level
// so it survives dbfile.vue remounts (nav away + back) → no refetch of already-
// indexed files. Only the durable "indexed" state is cached here; "unindexed" is
// re-verified per mount so a file that finished indexing while the page was away
// flips to indexed on return. Reactive so the badges update when entries change.
export const dbIndexedPaths: { [service_id: string]: Set<string> } = reactive({});

// Negative-lookup memo for the AI-index badge: storagePath → Date.now() of the
// last authoritative "unindexed" answer, per service. UNindexed is deliberately
// NOT durable (a file may get indexed while the page is away), but without a
// short memo every dbfile remount re-fired a src::/done:: getUniqueId pair per
// unindexed file — bouncing chat ↔ files duplicated the whole sweep each time.
// Entries are trusted for DB_UNINDEXED_TTL_MS and are deleted by every path
// that can change the answer (a finishing run, a delete, manual refresh).
export const dbUnindexedChecked: { [service_id: string]: Map<string, number> } = {};
export const DB_UNINDEXED_TTL_MS = 60_000;

// ─── indexing marker sweep (done:: + run::) ──────────────────────────────────
// ONE records query answers, for the whole service, both "which files are
// confirmed fully indexed" (done:: markers) and "which indexing runs exist and
// how did they end" (run:: records, with their status). It queries the
// __INDEXING__ table directly because that is where BOTH marker kinds live and
// because a getUniqueId sweep cannot work here: the SDK strips record data from
// its results, and the run records' whole value IS their data (status/started).
// Both the files page's badges and the chat page's rows read this instead of
// asking per file. Short TTL + explicit invalidation on completion events;
// in-flight deduped.
import { skapi } from "@/main";
import { normalizeStoragePath } from "@/code/utils";

export type IndexRunRecordInfo = {
    status: 'working' | 'done' | 'error' | 'cancelled';
    filename?: string;
    started?: number;
    finished?: number;
    error?: string;
    /** Chat that owns this run (project + platform). Absent on records minted
     *  before it was stamped; the display shows those in both platforms. */
    platform?: 'claude' | 'openai';
    /** user_id of the record's author — the user whose run this is. The chat
     *  filters stubs to the viewer's own runs; badges use every run. */
    owner?: string;
};
export type IndexMarkerSweep = {
    /** Storage paths carrying a done:: completion marker. */
    done: Set<string>;
    /** Storage path → newest run record. */
    runs: Map<string, IndexRunRecordInfo>;
    /** The page cap was hit before the table ended: `done` is NOT a complete
     *  set, so absence proves nothing and per-file fallbacks must stay on. */
    partial: boolean;
};

const MARKER_SWEEP_TTL_MS = 30_000;
// Records, not ids: up to TWO per file (done:: + run::), so the page budget is
// doubled over the old id sweep's 5 to keep the same file capacity.
const MARKER_SWEEP_MAX_PAGES = 10;
const markerSweepCache: { [service_id: string]: { res: IndexMarkerSweep; at: number } } = {};
const markerSweepInflight: { [service_id: string]: Promise<IndexMarkerSweep> } = {};

export function invalidateDoneMarkerSweep(serviceId: string): void {
    delete markerSweepCache[serviceId];
}

export function sweepIndexMarkers(serviceId: string): Promise<IndexMarkerSweep> {
    const cached = markerSweepCache[serviceId];
    if (cached && Date.now() - cached.at < MARKER_SWEEP_TTL_MS) return Promise.resolve(cached.res);
    if (markerSweepInflight[serviceId]) return markerSweepInflight[serviceId];
    const p = (async () => {
        const res: IndexMarkerSweep = { done: new Set<string>(), runs: new Map<string, IndexRunRecordInfo>(), partial: false };
        let fetchMore = false;
        for (let page = 0; page < MARKER_SWEEP_MAX_PAGES; page++) {
            const page_res: any = await (skapi as any).getRecords(
                { service: serviceId, table: { name: "__INDEXING__", access_group: "authorized" } },
                { limit: 1000, fetchMore, ascending: false },
            );
            for (const rec of page_res?.list || []) {
                const uid = String(rec?.unique_id || "");
                if (uid.startsWith("done::")) {
                    res.done.add(uid.slice("done::".length));
                } else if (uid.startsWith("run::")) {
                    const path = uid.slice("run::".length);
                    const d = rec?.data || {};
                    const status = String(d.status || "");
                    if (path && (status === 'working' || status === 'done' || status === 'error' || status === 'cancelled')) {
                        // Newest-first listing: the first record seen for a path wins.
                        if (!res.runs.has(path)) {
                            res.runs.set(path, {
                                status: status as IndexRunRecordInfo['status'],
                                filename: typeof d.filename === 'string' ? d.filename : undefined,
                                started: typeof d.started === 'number' ? d.started : undefined,
                                finished: typeof d.finished === 'number' ? d.finished : undefined,
                                error: typeof d.error === 'string' ? d.error : undefined,
                                platform: d.platform === 'claude' || d.platform === 'openai' ? d.platform : undefined,
                                owner: typeof rec?.user_id === 'string' ? rec.user_id : undefined,
                            });
                        }
                    }
                }
            }
            if (page_res?.endOfList !== false) break;
            fetchMore = true;
            // Cap reached with more pages left: what we have is usable but the
            // done-set is incomplete — readers must not treat absence as proof.
            if (page === MARKER_SWEEP_MAX_PAGES - 1) res.partial = true;
        }
        markerSweepCache[serviceId] = { res, at: Date.now() };
        return res;
    })();
    markerSweepInflight[serviceId] = p;
    // Not .finally(): the project's TS lib config predates it. Failures are not
    // cached — the next caller retries.
    p.then(() => { delete markerSweepInflight[serviceId]; }, () => { delete markerSweepInflight[serviceId]; });
    return p;
}

/** Back-compat view over sweepIndexMarkers for the done-set consumers. */
export function sweepDoneMarkers(serviceId: string): Promise<Set<string>> {
    return sweepIndexMarkers(serviceId).then((r) => r.done);
}

/** Patch the CACHED sweep after a client-side run:: write, so re-observations
 *  within the cache TTL see the new status instead of re-firing the write (a
 *  remount reads the cached 'working' for up to 30s otherwise, and every read
 *  re-minted). */
export function noteRunRecordStatus(
    serviceId: string,
    path: string,
    status: IndexRunRecordInfo['status'],
    meta?: { filename?: string; started?: number; platform?: 'claude' | 'openai'; owner?: string },
): void {
    const cached = markerSweepCache[serviceId];
    if (!cached) return;
    // A NEW object: the same record objects are copied into the chat's reactive
    // stub map, and mutating one in place changed what renders with no
    // notification. Timestamps are preserved so the row keeps its position.
    const cur = cached.res.runs.get(path);
    // When there is no cached record this INVENTS one, and an invented record with no
    // `platform` is shown in BOTH chats (the platform gate can only skip a record that
    // states its platform) - so a run finished under one model painted a green
    // "Indexed" row into the other model's conversation. The caller knows all of this;
    // it just was not being asked for it.
    cached.res.runs.set(path, cur
        ? {
            ...cur,
            status,
            finished: status === 'working' ? cur.finished : (cur.finished ?? Date.now()),
            ...(meta && meta.filename && !cur.filename ? { filename: meta.filename } : {}),
            ...(meta && typeof meta.started === 'number' && cur.started === undefined ? { started: meta.started } : {}),
            ...(meta && meta.platform && !cur.platform ? { platform: meta.platform } : {}),
            ...(meta && meta.owner && !cur.owner ? { owner: meta.owner } : {}),
        }
        : {
            status,
            finished: status === 'working' ? undefined : Date.now(),
            ...(meta && meta.filename ? { filename: meta.filename } : {}),
            ...(meta && typeof meta.started === 'number' ? { started: meta.started } : {}),
            ...(meta && meta.platform ? { platform: meta.platform } : {}),
            ...(meta && meta.owner ? { owner: meta.owner } : {}),
        });
}

// Index verdicts OBSERVED BY THE CHAT PAGE, per service: group key (storage
// path, or bare filename for old prompts) → the newest run's settled verdict.
// Written by agent.vue's row recorder; read by dbfile.vue so a first visit
// paints badges from what the chat already established instead of starting
// from grey.
//   'confirmed' — deterministic completion: a SINGLE-pass file whose one pass
//     settled ("a settled pass IS the end", engine indexing_groups). Durable-
//     grade: dbfile may promote it straight into dbIndexedPaths with no fetch.
//     (Single-pass runs never mint a done:: marker, so dbfile's own path for
//     them was the slow two-answer ladder.)
//   'probable' — a worker/client run's green, which rests on ONE idle queue
//     answer (a self-correcting display verdict, per the engine's own rules).
//     dbfile paints it immediately but STILL verifies in the background before
//     anything becomes durable.
// A run seen RUNNING (or stopped/cancelled/errored) deletes the entry.
export const chatIndexVerdicts: { [service_id: string]: Map<string, { state: 'confirmed' | 'probable'; at: number }> } = {};
export const CHAT_VERDICT_TTL_MS = 10 * 60_000;

// Persistent extracted-media index for dbfile.vue, keyed by service id → dir →
// { fileName: true } for files that have extracted media. Module-level so it
// survives dbfile.vue remounts (nav away + back) → the __MEDIA__ listing isn't
// re-fetched every time the page is entered. Freshness is event-driven: the
// manual refresh button, a finishing indexing run (the only thing that creates
// new media), and the stale-arrival trigger re-ask; everything else trusts the
// cache. Reactive so the media chevrons update when entries land.
export const dbMediaIndex: {
    [service_id: string]: { [dir: string]: { [name: string]: true } };
} = reactive({});
// In-flight guard for the cache above. Module-level so an unmount + remount
// can't start a second fetch for a dir whose listing is already on the wire.
export const dbMediaLoading: { [service_id: string]: { [dir: string]: boolean } } = {};

type UploadTarget = 'db' | 'host';
type UploadedItemInfo = {
    file: File;
    relPath: string;       // path relative to currentDir (e.g. "sub/x.png" or "x.png")
    storagePath: string;   // full storage key (currentDir + relPath)
    target: UploadTarget;
    storeKey?: string;
    // Project this file was UPLOADED to, captured when the item was enqueued.
    // The caller's cross-project guard is worthless without it: reading
    // currentService on both sides of a comparison always compares equal, so a
    // file uploaded to A while the user reads B was indexed under B.
    serviceId?: string;
    // Bytes of the file this upload OVERWROTE at the same path, or 0 for a new
    // file. Lets the caller apply the true storage delta (file.size -
    // previousSize) instead of over-counting overwrites.
    previousSize?: number;
    // True when the existing file was kept (user chose "Reindex") — no
    // bytes were uploaded, so the caller should reindex the existing file and
    // NOT change storage usage.
    reindexOnly?: boolean;
    // True when this upload REPLACED an existing file (server reported EXISTS
    // and the user chose "Overwrite"). This is the AUTHORITATIVE overwrite
    // signal: `previousSize` is derived from the client-side pager cache and is
    // 0 whenever that cache doesn't hold the old item (folder never opened, a
    // stale/paginated listing, a file written by the agent or another session)
    // or when the old file was genuinely empty — so it must not be used on its
    // own to decide whether stale AI-index records need clearing.
    overwrote?: boolean;
    // This file's row in the persisted queue, handed to the per-item hook so the
    // hook can close it out only once ITS work is done. The row is no longer
    // deleted when the bytes land: it is moved to the 'index' phase and becomes
    // the durable record of "stored, not yet indexed", which is what makes the
    // index setup survive a reload. A hook that gets an id owns it.
    persistId?: string;
};
// Per-file decision returned by onBeforeItem when an upload hits an existing
// file at the same path: skip it, keep & reindex, or replace.
type OverwriteDecision = "reindex" | "overwrite" | "skip";

// Everything the BATCH-level pre-upload hook is told about the upload it is
// being asked to approve. An object rather than a bare File[] for the same
// reason UploadedItemInfo is one: that type grew four fields after it was
// written, and a positional argument cannot grow without touching every caller.
//
// Exported because the hook is implemented in a component (dbfile.vue), which
// needs the type to annotate it.
export type UploadBatchInfo = {
    /** Every file about to be queued, in queue order. A COPY of the caller's
     *  list: filtering it does NOT filter the upload (the queue is still built
     *  from the caller's own list), so the only way for a hook to stop files
     *  from going up is to return the cancel value. */
    files: File[];
    target: UploadTarget;
    /** Folder the batch lands in, with its trailing "/" ("" at the root). */
    currentDir: string;
    storeKey?: string;
    /** Project the batch is going to, captured BEFORE the hook is awaited (a
     *  hook is a modal, and the user can navigate while it is open). */
    serviceId: string;
};

/**
 * The batch-level pre-upload hook: ONE question for the whole batch, asked
 * before anything is queued and before a byte moves.
 *
 * It exists because onBeforeItem cannot do this job. That one runs inside the
 * upload worker, once per file, after that file's bytes are already on the wire,
 * so a question that is really about the batch (who may ask questions about
 * these files once they are indexed) got asked thirty times, after the fact,
 * about files that had already uploaded.
 *
 * Return nothing (undefined) or any truthy value to go ahead. Return false, or
 * any other falsy value that is not undefined (null, "", 0), to CANCEL: nothing
 * is queued and nothing uploads. See runBeforeBatch for why the cancel test is
 * that shape and what happens to the caller's callback.
 */
type BeforeBatchHook = (info: UploadBatchInfo) => unknown;
type QueueItem = {
    file: File;
    currentDir: string;
    target: UploadTarget;
    foldersRef?: any;
    storeKey?: string;
    contentTypeMapping?: { [fname: string]: string };
    callback?: (hasOverwritten: boolean) => void;
    onItemUploaded?: (info: UploadedItemInfo) => void | Promise<void>;
    // Optional pre-upload hook (db target). When an existing file is detected
    // at the same path, it is awaited to decide keep-&-reindex vs overwrite.
    onBeforeItem?: (info: UploadedItemInfo) => Promise<OverwriteDecision>;
    // 1-based position of this file within the batch, for the per-file queue
    // display (so concurrent slots show distinct "3/11", "4/11" etc.).
    position?: number;
    // Path relative to currentDir. Set explicitly because a File restored from
    // the persisted queue has LOST its webkitRelativePath (that property does not
    // survive a structured clone), so a resumed folder upload would otherwise
    // flatten every file into the root.
    relPath?: string;
    // Key of this item's row in the persisted queue, deleted once the item
    // settles. Absent for targets that are not persisted.
    persistId?: string;
    // Service this item was enqueued FOR, captured at enqueue. Not re-read from
    // currentService later: the queue is a module singleton that keeps draining
    // across route changes, so by the time an item settles the app may be showing
    // a different project, and a row filed under that one is a row the resume
    // offer for this project will never find.
    serviceId?: string;
    // The Service INSTANCE this item was enqueued for, for the same reason and a
    // sharper one: uploadHostFiles signs each file's destination off the instance
    // it is called on, so reading `currentService.value` when the bytes finally go
    // out puts them in whatever project the user has since wandered to. agent.vue
    // pins the service at Send time for exactly this; this queue outlives routes
    // by design, so it has more need of it, not less.
    service?: any;
};

// Per-target upload state. The two UI pages (hosting.vue and dbfile.vue)
// each watch their own target's progress/count so a db-file upload never
// bleeds into the hosting view and vice versa.
//
// For the db target there is one of these PER PROJECT, not one in total. The
// queue is a module singleton that keeps draining across route changes, so a
// single shared queue meant a second project's upload was appended behind the
// first project's entire batch and could not start until it finished — while
// the progress rows on either page showed whatever file some worker happened to
// be on, from either project. Uploads to different projects have nothing to
// serialize on: separate destinations, separate storage, separate listings.
type TargetState = {
    target: UploadTarget;
    /** Project this state belongs to. "" for the host target (one site at a time). */
    serviceId: string;
    queue: QueueItem[];
    activeWorkers: number;
    hasOverwrittenFiles: boolean;
    activeCallbacks: Set<(hasOverwritten: boolean) => void>;
    uploadCount: number[]; // [finished, total]
    uploadProgress: any;   // { name, progress, fileSize, target } — host target only
    /** One entry per concurrent db worker; empty `name` = idle. Host target: []. */
    slots: UploadSlot[];
    /** Files that failed in the batch currently draining (db target). */
    failed: DbUploadFailure[];
    /** Snapshot of the last settled batch, watched by the page (db target). */
    result: { token: number; failed: DbUploadFailure[]; overwritten: boolean };
};

const makeTargetState = (target: UploadTarget, serviceId: string): TargetState => ({
    target,
    serviceId,
    queue: [],
    activeWorkers: 0,
    hasOverwrittenFiles: false,
    activeCallbacks: new Set(),
    uploadCount: reactive([0, 0]) as unknown as number[],
    uploadProgress: reactive({}),
    // Up to DB_CONCURRENCY files upload concurrently for the db target; each
    // slot tracks one in-flight file. `progress` is null until the first
    // byte-progress event: every upload opens with a get-signed-url round trip
    // that reports nothing, so null distinguishes "preparing" (barber pole)
    // from a genuine 0%.
    slots: target === 'db'
        ? (reactive(
            Array.from({ length: DB_CONCURRENCY }, () => ({ name: '', progress: null, fileSize: 0, position: 0 })),
        ) as unknown as UploadSlot[])
        : [],
    failed: [],
    result: reactive({ token: 0, failed: [] as DbUploadFailure[], overwritten: false }),
});

/**
 * Persisted ids of the items a worker is uploading RIGHT NOW.
 *
 * The queue alone cannot answer that: an item in progress has been shifted off
 * it, and its persisted row survives until the item settles. Without this, the
 * resume offer treats a file that is mid-upload in this very page as an
 * abandoned one — see inFlightUploadIds.
 */
const activeUploadIds = new Set<string>();

/** Stores already reported as having no folder cache. See the warn below. */
const warnedMissingFolderCache = new Set<string>();

/**
 * The persisted row for a queue item, metadata only unless told otherwise.
 *
 * Exists because two paths now have to be able to CREATE a row rather than patch
 * one: a file whose bytes were too large to mirror has none, and both the index
 * handover and the failure record are useless if they silently patch nothing.
 */
function persistedRowFor(
    item: QueueItem,
    f: File,
    currentDir: string,
    relPath: string,
    withFile?: boolean,
): PersistedUpload {
    return {
        id: item.persistId as string,
        serviceId: item.serviceId as string,
        target: 'db',
        storeKey: item.storeKey,
        currentDir,
        relPath,
        name: f.name,
        type: f.type || "",
        size: f.size,
        lastModified: (f as any).lastModified || 0,
        addedAt: Date.now(),
        // A retry needs the bytes, but the reason this row is being created at all
        // is that these bytes were too big to keep. Under the mirror limit we keep
        // them and the retry costs the user nothing; over it, the row is a record
        // and the retry has to start from the file again.
        ...(withFile && f.size <= PERSIST_MAX_FILE_BYTES ? { file: f } : {}),
    };
}

/**
 * Rows handed to the mirror that have NOT settled yet.
 *
 * The mirror is not awaited, so a small file can upload and delete its row
 * before that row is written. See persistPendingUploads' `stillPending`.
 */
const awaitingMirror = new Set<string>();

/**
 * Every persisted upload this page still owns: queued, or in a worker's hands.
 *
 * The resume offer MUST subtract these. The upload queue is a module singleton
 * and keeps running across route changes, while a persisted row is only deleted
 * when its item settles — so leaving the file page mid-upload and coming back
 * re-offered the very files that were still uploading, and accepting pushed the
 * SAME items into the SAME queue a second time. With db uploads running several
 * workers, the duplicate then uploaded alongside the original and fired
 * onItemUploaded twice, which is two index requests for one file: two collapsed
 * rows in the chat, the document indexed twice, and every record saved twice.
 */
export function inFlightUploadIds(): Set<string> {
    const ids = new Set<string>(activeUploadIds);
    for (const state of allUploadStates()) {
        for (const item of state.queue) {
            if (item.persistId) ids.add(item.persistId);
        }
    }
    return ids;
}

// One slot = one concurrent upload worker's current file.
export type UploadSlot = { name: string; progress: number | null; fileSize: number; position: number };

// Up to 4 files upload concurrently per project for the db target.
const DB_CONCURRENCY = 4;

// A single failed db upload: its path plus the original error's code/message so
// the db-file page can group failures by cause (mirrors agent.vue's chatbox
// upload-error report, which records err.code / err.message per attachment).
export interface DbUploadFailure {
    name: string;
    code: string;
    message: string;
}

// The hosting page uploads a site to whichever project is open, one at a time,
// so it keeps a single state. Db uploads get one state PER PROJECT — see
// TargetState.
const hostState: TargetState = makeTargetState('host', '');
const dbStates = new Map<string, TargetState>();

/**
 * The db upload state for a project, created on first use.
 *
 * Keyed by service id so two projects uploading at once never share a queue, a
 * worker pool, a progress row or a batch: dbfile.vue reads only the state of the
 * project it is showing, and a batch that stalls (a file waiting on this
 * project's "already exists" prompt, say) can only ever hold up its own project.
 */
export function dbUploadStateFor(serviceId: string): TargetState {
    const key = serviceId || "";
    let state = dbStates.get(key);
    if (!state) {
        state = makeTargetState('db', key);
        dbStates.set(key, state);
    }
    return state;
}

/** Progress slots of a project's db uploads (one entry per concurrent worker). */
export const dbUploadSlotsFor = (serviceId: string): UploadSlot[] => dbUploadStateFor(serviceId).slots;
/** [finished, total] of a project's db uploads. */
export const dbUploadCountFor = (serviceId: string): number[] => dbUploadStateFor(serviceId).uploadCount;
/** Last settled db batch of a project — `token` bumps on every completion. */
export const dbUploadResultFor = (serviceId: string): { token: number; failed: DbUploadFailure[]; overwritten: boolean } =>
    dbUploadStateFor(serviceId).result;

/**
 * True while a project's db batch is still running: a worker has a file in hand or
 * files are still queued. The Files page asks this on mount, because the queue
 * outlives the route: leaving mid-upload and coming back must not report the
 * batch's own files as unfinished work.
 */
export function dbUploadInFlight(serviceId: string): boolean {
    const state = dbStates.get(serviceId || "");
    return !!state && (state.activeWorkers > 0 || state.queue.length > 0);
}

/** Every live upload state: the host one plus one per project. */
function allUploadStates(): TargetState[] {
    return [hostState, ...dbStates.values()];
}

// Hosting-page bindings (unchanged export names for backward compatibility).
export const uploadCount = hostState.uploadCount;
export const uploadProgress = hostState.uploadProgress;

function reportDbBatchDone(state: TargetState, overwritten = false) {
    const failed = [...state.failed];
    state.result.failed = failed;
    // Whether this batch replaced files already stored, for the page's storage
    // figure. Carried on the result and not only handed to the batch callback:
    // the callback belongs to the page INSTANCE that started the batch, and the
    // queue outlives it (a visit to the chat and back remounts the Files page),
    // so the result object, which every instance of the page binds to, is what
    // the one on screen learns the ending from.
    state.result.overwritten = overwritten;
    state.result.token++;
    state.failed.length = 0;
    // The batch ran to the end, so every file was attempted whether or not the
    // browser managed to remember it. The untracked note exists to warn about
    // files lost to an INTERRUPTED batch, so it would be a false alarm now.
    //
    // TWO things this must NOT do. It must not clear whatever project happens to
    // be on screen: the queue outlives the route (that is why an item carries its
    // own serviceId), so a batch started in A and finishing while the user reads B
    // used to wipe B's warning and leave A's standing forever. And it must not
    // clear at all when files FAILED: an unmirrored file that failed has no row,
    // so that note is the only durable trace it ever existed.
    if (failed.length) return;
    if (state.serviceId) clearUntrackedUploads(state.serviceId);
}

async function processUploadQueue(state: TargetState) {
    const target = state.target;
    const maxWorkers = target === 'db' ? DB_CONCURRENCY : 1;
    if (state.activeWorkers >= maxWorkers) return;
    state.activeWorkers++;
    // First worker of a batch: hold the Web Lock (so a hidden tab is not frozen
    // mid-batch) and arm the unload warning. Released when the last worker exits.
    // Named per project: the lock is exclusive, so a single shared name meant
    // only the FIRST tab uploading held one and every other project's tab was a
    // freeze candidate the moment it went to the background.
    if (state.activeWorkers === 1) acquireUploadKeepalive(state.serviceId);

    // Each db worker owns one progress slot for its entire lifetime.
    // Host target uses the single state.uploadProgress object (slotIdx === -1).
    const slotIdx = target === 'db' ? state.slots.findIndex(s => !s.name) : -1;

    try {
        while (state.queue.length > 0) {
            let item = state.queue.shift()!;
            // In a worker's hands now, and off the queue — so only this set can
            // still say the file is being uploaded. See inFlightUploadIds.
            if (item.persistId) activeUploadIds.add(item.persistId);
            let f = item.file;
            let currentDir = item.currentDir;

            // Composed at capture so the "src::" id below and the S3 key agree. The key is
            // derived from the File itself inside the SDK, so a normalized relPath alone would
            // change ONLY the record: it is also passed as the FormData filename at the upload
            // call, which is what the SDK reads.
            const relPath = normalizeStoragePath(item.relPath || f.webkitRelativePath || f.name);
            const progressName = currentDir + relPath;
            if (slotIdx >= 0) {
                state.slots[slotIdx].name = progressName;
                state.slots[slotIdx].progress = null; // preparing until bytes move
                state.slots[slotIdx].fileSize = f.size;
                state.slots[slotIdx].position = item.position || 0;
            } else {
                state.uploadProgress.name = progressName;
                state.uploadProgress.progress = 0;
                state.uploadProgress.fileSize = f.size;
                state.uploadProgress.target = target;
            }

            const storagePath = currentDir + relPath;
            // Settle = this item will not be UPLOADED again, whatever the
            // outcome. It does not mean the file is finished: for a db upload the
            // index setup runs after this, and its row is handed on rather than
            // deleted (keepRow). Every path that ends an item goes through here,
            // so no outcome can leave a ghost row behind to be re-offered.
            const settleItem = (keepRow?: boolean) => {
                if (item.persistId) {
                    // Whatever the mirror still owes this file, it is now stale.
                    awaitingMirror.delete(item.persistId);
                    if (!keepRow) void unpersistPendingUpload(item.persistId);
                    activeUploadIds.delete(item.persistId);
                }
                state.uploadCount[0]++;
            };

            /**
             * Hand this file's row from the upload phase to the index phase.
             *
             * AWAITED, and before the hook is fired. If the row still said
             * 'upload' when the tab died, the next visit would offer to upload
             * bytes that are already stored; if it were deleted (what used to
             * happen), nothing would record that the file still needs indexing.
             * The write is a few milliseconds against work that just took a
             * network round trip, and the guarantee rests on it landing first.
             */
            const handOffToIndexPhase = async (replacesIndex: boolean) => {
                if (!item.persistId) return;
                beginIndexSetup(item.persistId);
                try {
                    await updatePersistedUpload(
                        item.persistId,
                        {
                            phase: "index",
                            // Carried, not re-derived: a repair after a reload has
                            // no folder cache and no EXISTS answer, so without this
                            // it would index a replaced file WITHOUT clearing the
                            // old index and leave both versions' rows in place.
                            replacesIndex,
                            settledAt: Date.now(),
                            indexAttempts: 0,
                            error: undefined,
                        },
                        {
                            dropFile: true,
                            // A file whose bytes were too large to mirror has no
                            // row yet (see PERSIST_MAX_FILE_BYTES). It still needs
                            // one HERE: the row is what records that a stored file
                            // has not been indexed, and the repair pass has nothing
                            // else to find it by. Metadata only, so it is exempt
                            // from the mirror budget by construction.
                            createFrom: item.serviceId
                                ? persistedRowFor(item, f, currentDir, relPath)
                                : undefined,
                        },
                    );
                } catch (e) {
                    // Best effort: a file that cannot be recorded as pending is
                    // still better uploaded than not.
                }
            };

            // One upload attempt. Fresh FormData per attempt so a retry after an
            // EXISTS probe starts clean. check_existence makes the server reject
            // an already-present file (EXISTS) BEFORE uploading the bytes.
            const doUpload = (checkExistence: boolean) => {
                const fd = new FormData();
                // Third argument overrides the filename the SDK reads to build the S3 key.
                // Without it the key comes from the raw File (decomposed on macOS) while the
                // record says composed, which is exactly the split this fix exists to prevent.
                // It also restores the folder prefix for an upload RESUMED from IndexedDB, where
                // the rehydrated File has lost webkitRelativePath.
                fd.append("files[]", f, relPath);
                // The service this item was ENQUEUED for. See QueueItem.service.
                const svc = item.service || currentService.value;
                return svc.uploadHostFiles(fd, {
                    contentTypeMapping: item.contentTypeMapping,
                    prefix: currentDir,
                    target,
                    ...(checkExistence ? { check_existence: true } : {}),
                    progress: (e) => {
                        if (e.status !== "progress") return;
                        const percent = Math.floor(e.progress);
                        if (slotIdx >= 0) {
                            state.slots[slotIdx].progress = percent;
                        } else {
                            state.uploadProgress.progress = percent;
                        }
                    },
                });
            };

            // One attempt, repeated on TRANSPORT failures only (see
            // isRetriableUploadError). Retrying the bytes is safe here: the
            // destination key is deterministic, so a repeat PUT overwrites rather
            // than duplicating, and nothing on this path writes a database row —
            // the AI index record is created later, by onItemUploaded. That is
            // what separates this from the request path where a blind 5xx retry
            // can double-post.
            //
            // One honest ambiguity: if a check_existence probe dies in transport
            // AFTER the server stored the bytes, the retry comes back EXISTS and
            // the user is asked to confirm an overwrite of a file they just
            // uploaded. Choosing Overwrite re-writes identical bytes, so the
            // conservative reading is the safe one and it is not silently
            // resolved here.
            const doUploadWithRetry = async (checkExistence: boolean) => {
                let lastErr: any;
                for (let attempt = 1; attempt <= UPLOAD_MAX_ATTEMPTS; attempt++) {
                    try {
                        return await doUpload(checkExistence);
                    } catch (err: any) {
                        lastErr = err;
                        // EXISTS is the probe's designed answer, not a failure.
                        if ((err?.code ?? err?.body?.code) === 'EXISTS') throw err;
                        if (attempt >= UPLOAD_MAX_ATTEMPTS || !isRetriableUploadError(err)) throw err;
                        await sleep(uploadRetryDelayMs(attempt));
                    }
                }
                throw lastErr;
            };

            // True when the user chose "Reindex" for an existing file:
            // nothing uploaded, so skip the folder bookkeeping and storage delta.
            let reindexOnly = false;
            // True when the user chose "skip" for an existing file: leave it
            // untouched entirely — no upload, no reindex, no bookkeeping.
            let skipped = false;
            // True when the user chose "Overwrite" for an existing file. Set off
            // the SERVER's EXISTS probe, so unlike `previousSize` it is reliable
            // regardless of what the client's folder cache happens to hold.
            let overwrote = false;

            try {
                if (target === 'db' && item.onBeforeItem) {
                    // Probe with check_existence; on EXISTS ask how to resolve.
                    try {
                        await doUploadWithRetry(true);
                    } catch (probeErr: any) {
                        const code = probeErr?.code ?? probeErr?.body?.code;
                        if (code !== 'EXISTS') throw probeErr;
                        const choice = await item.onBeforeItem({ file: f, relPath, storagePath, target, storeKey: item.storeKey });
                        if (choice === 'overwrite') {
                            overwrote = true;      // authoritative: server said EXISTS
                            // Same reason as `overwrote`: the bookkeeping below
                            // only flags this when the old item happened to be in
                            // the folder cache, so flag it here where we KNOW.
                            state.hasOverwrittenFiles = true;
                            await doUploadWithRetry(false); // replace the existing file
                        } else if (choice === 'skip') {
                            skipped = true;        // leave it untouched
                        } else {
                            reindexOnly = true;    // keep it; Reindex
                        }
                    }
                } else {
                    await doUploadWithRetry(false);
                }
            } catch (err: any) {
                if (target === 'db') {
                    // Don't abort the batch. Record the failure and its cause,
                    // count it as processed (so the progress bar still reaches
                    // 100% and the slot frees for the next file), then continue
                    // with the rest of the queue. All failures are surfaced
                    // together once the batch settles (see reportDbBatchDone /
                    // dbUploadResult). Code/message are extracted the same way
                    // agent.vue's recordAttachmentError does, so the shared
                    // groupAttachmentFailures helper can group them by cause.
                    const failure = {
                        name: progressName,
                        code: (err && (err.code || err?.body?.code)) || "",
                        message:
                            (err && (err.message || err?.body?.message)) ||
                            (typeof err === "string" ? err : ""),
                    };
                    state.failed.push(failure);
                    // Also record it DURABLY. state.failed is a module-local
                    // array read by one component-scoped watcher that only fires
                    // when the whole batch settles, so for a long batch every
                    // failure lived in memory alone for hours: a reload, or the
                    // batch finishing while the user was on another route, and
                    // the file was neither uploaded nor offered back nor ever
                    // named. The row keeps its blob so a retry needs nothing from
                    // the user.
                    if (item.persistId) {
                        void updatePersistedUpload(
                            item.persistId,
                            {
                                phase: "failed",
                                error: { code: failure.code, message: failure.message },
                                settledAt: Date.now(),
                            },
                            // Same reason the handover carries one: a file whose
                            // bytes were too large to mirror has no row, and
                            // patching nothing is how a failure disappears
                            // completely (reportDbBatchDone then clears the note
                            // that was the only other trace of it). Keeps its blob
                            // ONLY if we still hold one; otherwise the row is the
                            // record, and the retry has to come from the user.
                            item.serviceId ? { createFrom: persistedRowFor(item, f, currentDir, relPath, true) } : undefined,
                        );
                    }
                    settleItem(true);
                    continue;
                }
                // Host target: abort the whole batch on the first error.
                // First worker to hit an error fires callbacks; others see empty activeCallbacks and exit.
                if (state.activeCallbacks.size > 0) {
                    state.uploadCount[0] = 0;
                    state.uploadCount[1] = 0;
                    state.uploadProgress.name = "";
                    state.uploadProgress.progress = 0;
                    state.uploadProgress.fileSize = 0;
                    state.uploadProgress.target = "";
                    state.queue.length = 0;
                    state.hasOverwrittenFiles = false;
                    let callbacks = [...state.activeCallbacks];
                    state.activeCallbacks.clear();
                    for (let cb of callbacks) {
                        cb(false);
                    }
                }
                return;
            }

            // "Skip" — existing file left untouched. Nothing uploaded, no
            // reindex, no per-item hook, no folder bookkeeping or storage delta.
            // Count it processed (so the progress bar completes and the slot
            // frees), then continue with the rest of the queue.
            if (skipped) {
                settleItem();
                continue;
            }

            // "Reindex" — existing file kept, nothing uploaded. Fire the
            // per-item hook so the caller can reindex it, then skip folder
            // bookkeeping and the storage delta entirely.
            if (reindexOnly) {
                if (item.onItemUploaded) {
                    // Same handover as a real upload: the bytes were already
                    // there, but the indexing dispatch that follows is exactly as
                    // interruptible, and exactly as invisible if it is lost.
                    await handOffToIndexPhase(true);
                    try {
                        void item.onItemUploaded({ file: f, relPath, storagePath, target, storeKey: item.storeKey, serviceId: item.serviceId, reindexOnly: true, persistId: item.persistId });
                    } catch (cbErr) {
                        console.error("onItemUploaded callback failed", cbErr);
                    }
                    settleItem(true);
                    continue;
                }
                settleItem();
                continue;
            }

            // Upload succeeded — do bookkeeping in the while loop (awaited) so
            // folder insertions for every file are guaranteed complete before
            // "all done" fires. Previously this ran inside the progress callback
            // which was not awaited, causing a race where getFileList() could be
            // called before concurrent insertions finished.

            let fPathArr = relPath.split("/");
            let flName = fPathArr.pop();
            let fPath = currentDir + fPathArr.join("/");
            if (fPath.slice(-1) === "/") {
                fPath = fPath.slice(0, -1);
            }

            // Size of the file this upload overwrites at the same path (0 if
            // new). Captured in the folder bookkeeping below — where the pager
            // still holds the old item before editItem replaces it — so the
            // per-item hook (fired after bookkeeping) can report the true
            // storage delta instead of the full new size.
            let previousFileSize = 0;

            // Optional-chained: main.vue sets `currentService.value = null` on every
            // project switch, and this line runs in a worker loop that keeps
            // draining across route changes. A null dereference here throws in the
            // same place, and for the same reason, as the bug this block fixes.
            let _storeKey = item.storeKey || currentService.value?.service?.subdomain;
            let _foldersRef = item.foldersRef || serviceFolders;
            // The listing cache for THIS store, which may not exist.
            //
            // It is a view's cache, not a source of truth: dbfile.vue creates the
            // bucket for the project it was set up with, hosting.vue for the
            // subdomain it rendered, and the upload queue is a module singleton
            // that outlives both. Every access below already tolerated a missing
            // FOLDER and skipped its update; only the bucket itself was assumed,
            // and reading a path off `undefined` is what threw a TypeError out of
            // this worker, which then died with the rest of the batch still queued
            // (see the try/catch below for why that was so expensive).
            //
            // No bucket simply means nothing on screen is showing this store, so
            // there is nothing to keep in sync: whoever opens it next fetches the
            // listing from the server.
            const storeFolders = _foldersRef ? _foldersRef[_storeKey] : undefined;
            if (!storeFolders && !warnedMissingFolderCache.has(String(_storeKey))) {
                // ONCE per store, not once per file: the condition is a property of
                // the store, so a 10k-file batch would otherwise print 10k identical
                // lines, and console spam of that size is its own performance bug.
                //
                // Named, because the three ways to get here are hard to tell apart
                // after the fact: a resumed row with no storeKey (it is optional),
                // a store the mounted view never created a bucket for, or a host
                // upload whose subdomain bucket has not been built yet.
                warnedMissingFolderCache.add(String(_storeKey));
                console.warn(
                    "[upload] no folder cache for this store; skipping listing bookkeeping",
                    { storeKey: _storeKey, known: _foldersRef ? Object.keys(_foldersRef) : null },
                );
            }

            // Bookkeeping is a UI-cache update on a file whose BYTES ARE ALREADY
            // STORED. It must never be able to end the upload: the throw used to
            // escape the while loop, and since the only wrapper is a finally, the
            // worker exited with the queue still full, the completion callback
            // never fired (uploadCount never reached its total), the file never
            // reached handOffToIndexPhase so it was stored but never indexed, and
            // its row still said 'upload' so the next visit offered to upload it
            // again. All of that from a stale listing cache.
            try {
                let fileObj: any = {
                    name: flName,
                    size: f.size,
                    path: _storeKey + (fPath === "" ? "" : "/" + fPath),
                    upl: new Date().getTime(),
                };

                let fPathSplit = fPath.split("/");
                // Root pager of this store. Was `_foldersRef[_storeKey]["!"].pager`
                // with no guard at all, unlike every other branch here.
                const rootPager = storeFolders?.["!"]?.pager;

                if (fPathSplit.length === 1) {
                    if (fPathSplit[0] === "") {
                        if (rootPager) {
                            let folder = rootPager;
                            if (folder.list?.[flName]) {
                                previousFileSize = folder.list[flName].size || 0;
                                state.hasOverwrittenFiles = true;
                                await folder.editItem(fileObj);
                            } else {
                                await folder.insertItems(fileObj);
                            }
                        }
                    } else {
                        if (rootPager) {
                            let folder = rootPager;
                            if (!folder.list?.["#" + fPathSplit[0]]) {
                                let obj = Object.assign({}, fileObj, {
                                    name: "#" + fPathSplit[0],
                                    cnt: 0,
                                    size: 0,
                                    path: _storeKey,
                                });
                                await folder.insertItems(obj);
                            } else {
                                let obj = folder.list?.["#" + fPathSplit[0]];
                                obj.size = (obj.size || 0) + f.size;
                                obj.cnt = (obj.cnt || 0) + 1;
                                await folder.editItem(obj);
                            }
                        }

                        if (storeFolders?.[fPathSplit[0]]) {
                            let folder = storeFolders[fPathSplit[0]].pager;
                            if (folder.list?.[flName]) {
                                previousFileSize = folder.list[flName].size || 0;
                                state.hasOverwrittenFiles = true;
                                await folder.editItem(fileObj);
                            } else {
                                await folder.insertItems(fileObj);
                            }
                        }
                    }
                } else {
                    // has nested folders
                    let pathIdx = fPathSplit.length;
                    let fp = fPathSplit.slice(0, pathIdx).join("/");
                    if (storeFolders?.[fp]) {
                        let folder = storeFolders[fp].pager;
                        if (folder.list?.[flName]) {
                            previousFileSize = folder.list[flName].size || 0;
                            state.hasOverwrittenFiles = true;
                            await folder.editItem(fileObj);
                        } else {
                            await folder.insertItems(fileObj);
                        }
                    }
                    do {
                        if (pathIdx) {
                            fileObj = Object.assign({}, fileObj);
                            let fp = fPathSplit.slice(0, pathIdx);
                            let fldName = "#" + fp.pop();
                            let fpStr = fp.join("/") || "!";
                            fileObj.name = fldName;
                            if (storeFolders?.[fpStr]) {
                                let folder = storeFolders[fpStr].pager;
                                if (folder.list?.[fldName]) {
                                    let obj = folder.list?.[fldName];
                                    obj.size = (obj.size || 0) + f.size;
                                    obj.cnt = (obj.cnt || 0) + 1;
                                    obj.path = _storeKey + (fpStr.replace("!", "") ? "/" + fpStr : "");
                                    await folder.editItem(obj);
                                } else {
                                    fileObj.cnt = 1;
                                    fileObj.size = f.size;
                                    fileObj.path = _storeKey + (fpStr.replace("!", "") ? "/" + fpStr : "");
                                    await folder.insertItems(fileObj);
                                }
                            }
                        }
                    } while (pathIdx--);
                }
            } catch (bookErr) {
                // previousFileSize may be short, so the storage delta reported
                // below can overstate. That is a number on a page; losing the rest
                // of the batch is the user's files.
                console.error(
                    "[upload] folder bookkeeping failed; upload continues",
                    { storeKey: _storeKey, path: fPath },
                    bookErr,
                );
            }

            // Per-item hook: fired after this file's folder bookkeeping (so
            // previousSize is known) but still per-file, not awaited — the
            // caller acts on each finished file without waiting for the batch.
            if (item.onItemUploaded) {
                await handOffToIndexPhase(overwrote || previousFileSize > 0);
                try {
                    void item.onItemUploaded({
                        file: f,
                        relPath,
                        storagePath,
                        target,
                        storeKey: item.storeKey,
                        serviceId: item.serviceId,
                        previousSize: previousFileSize,
                        reindexOnly: false,
                        overwrote,
                        persistId: item.persistId,
                    });
                } catch (cbErr) {
                    console.error("onItemUploaded callback failed", cbErr);
                }
                // keepRow: the hook owns the row now and closes it out when the
                // indexing dispatch is accepted. Deleting it here is what left a
                // file uploaded, un-indexed, unrecorded and (because its index
                // record had already been written) badged as indexed.
                settleItem(true);
            } else {
                settleItem();
            }
        }
    } finally {
        state.activeWorkers--;
        // Last worker out releases the lock and disarms the unload warning.
        if (state.activeWorkers === 0) releaseUploadKeepalive(state.serviceId);
        if (slotIdx >= 0) {
            state.slots[slotIdx].name = '';
            state.slots[slotIdx].progress = null;
            state.slots[slotIdx].fileSize = 0;
            state.slots[slotIdx].position = 0;
        }

        // Fire the completion callback only when the last active worker exits.
        // At this point every worker's while loop has finished, so all folder
        // insertions are guaranteed complete (no race with concurrent callbacks).
        if (state.activeWorkers === 0 && state.uploadCount[0] > 0 && state.uploadCount[0] === state.uploadCount[1]) {
            if (!state.activeCallbacks.size) {
                if (target === 'db') reportDbBatchDone(state, state.hasOverwrittenFiles);
                return;
            }
            let callbacks = [...state.activeCallbacks];
            let overwritten = state.hasOverwrittenFiles;
            state.uploadCount[0] = 0;
            state.uploadCount[1] = 0;
            if (slotIdx < 0) {
                state.uploadProgress.name = "";
                state.uploadProgress.progress = 0;
                state.uploadProgress.fileSize = 0;
                state.uploadProgress.target = "";
            }
            state.hasOverwrittenFiles = false;
            state.activeCallbacks.clear();
            if (target === 'db') reportDbBatchDone(state, overwritten);
            for (let cb of callbacks) {
                cb(overwritten);
            }
        }
    }
}

/**
 * Folder a batch lands in, with its trailing "/" ("" at the root).
 *
 * Pulled out of uploadFiles because onDrop needs the SAME answer to describe the
 * drop to the batch hook, and two copies of this expression is how the two
 * entry points end up telling the user about two different folders.
 */
const resolveUploadDir = (dir?: string): string =>
    dir !== undefined
        ? (dir ? dir + "/" : "")
        : (currentDirectory.value ? currentDirectory.value + "/" : "");

/**
 * Ask the batch hook, once, and report whether the batch may proceed.
 *
 * Returns true to go ahead, false when the batch is CANCELLED. On a cancel it
 * has already settled the caller's `callback`, because nothing was queued and
 * therefore nothing will ever settle it later: the callback is what takes the
 * page out of its uploading state (dbfile.vue's onDbUploadDone, hosting.vue's
 * inline one), so leaving it unsettled is how a dismissed question turns into a
 * page that waits for an upload that will never happen. It receives `false`,
 * meaning "nothing was overwritten", which is true in the strongest possible
 * sense: nothing was uploaded at all. Every existing callback reads that value
 * only to decide whether to refetch storage usage, so a cancel costs a listing
 * refresh and nothing else.
 *
 * What counts as a cancel: any resolved value that is falsy but NOT undefined
 * (false, null, "", 0). undefined alone means "carry on", because that is what a
 * hook that simply asks something and returns nothing gives back. Testing only
 * for `=== false` would be the dangerous half-measure here: a dismissed modal
 * that resolves null is exactly the case this feature exists to stop, and under
 * that test all thirty files the user just declined to answer for would upload.
 *
 * A hook that THROWS also cancels. An exception is not an answer, and the whole
 * point of asking first is that the files do not move until there is one.
 */
async function runBeforeBatch(
    hook: BeforeBatchHook,
    info: UploadBatchInfo,
    callback?: (hasOverwritten: boolean) => void,
): Promise<boolean> {
    let decision: unknown;
    try {
        decision = await hook(info);
    } catch (err) {
        console.error("onBeforeBatch hook failed, batch cancelled", err);
        decision = false;
    }
    if (decision === undefined || decision) return true;
    if (callback) {
        try {
            callback(false);
        } catch (cbErr) {
            // A throwing callback must not turn a clean cancel into a rejected
            // uploadFiles()/onDrop() promise: neither page catches one, and the
            // drop handlers only clear their scrim in a finally.
            console.error("upload callback failed after a cancelled batch", cbErr);
        }
    }
    return false;
}

export let uploadFiles = async (
    files: File[],
    callback?: (hasOverwritten: boolean) => void,
    contentTypeMapping?: { [fname: string]: string },
    options?: {
        target?: UploadTarget;
        currentDir?: string;
        foldersRef?: any;
        storeKey?: string;
        onItemUploaded?: (info: UploadedItemInfo) => void | Promise<void>;
        onBeforeItem?: (info: UploadedItemInfo) => Promise<OverwriteDecision>;
        /** Asked ONCE for the whole batch, before anything is queued. Falsy but
         *  not undefined cancels the batch outright. See BeforeBatchHook. */
        onBeforeBatch?: BeforeBatchHook;
    }
) => {
    const target: UploadTarget = options?.target === 'db' ? 'db' : 'host';

    if (target !== 'db') {
        let sd = currentService.value.service.subdomain;
        if (!sd) {
            throw new Error("No subdomain found");
        }
        if ((sd && sd[0] === "*") || sd[0] === "+") {
            throw new Error("Cannot upload files to pending subdomain");
        }
    }

    // The project these bytes are going to, captured ONCE here and carried by
    // every item of this batch — including the queue they go on. A db upload is
    // scheduled per project, so starting one in a second project neither waits
    // for the first project's batch nor shows up on its page.
    //
    // Captured (with uploadService and currentDir) BEFORE onBeforeBatch is
    // awaited, not after. The hook is a modal, and a modal stays open for as
    // long as the user leaves it open: reading the live project or the on-screen
    // folder once it answers would file the batch under wherever the user
    // wandered to while deciding. With no hook there is no await here at all, so
    // this is the same read at the same moment it always was.
    const serviceId = currentService.value?.id || "";
    const uploadService = currentService.value;
    let currentDir = resolveUploadDir(options?.currentDir);

    // ONE question for the whole batch, before a single byte moves and before
    // anything is queued: no counter bumped, no callback registered, no
    // persisted row written, no worker spawned. That ordering is the feature: a
    // cancel has to leave this call as if it had never been made, or the user
    // who dismissed the question discovers their files went up anyway.
    //
    // The `if` guards the AWAIT, not just the hook. An unconditional await here
    // would push the queue push into a microtask for every existing caller too,
    // and onDrop deliberately does not await uploadFiles, so its promise would
    // resolve before a single item was queued (both file pages lower their drop
    // scrim on exactly that promise, on the premise that the progress rows have
    // something to show by then).
    if (options?.onBeforeBatch) {
        const proceed = await runBeforeBatch(
            options.onBeforeBatch,
            { files: Array.from(files), target, currentDir, storeKey: options?.storeKey, serviceId },
            callback,
        );
        if (!proceed) return;
    }

    const state = target === 'db' ? dbUploadStateFor(serviceId) : hostState;

    if (target === 'db' && state.activeWorkers === 0) {
        // Fresh db batch (nothing in flight): drop leftover failure records
        // from the previous batch so this batch reports only its own failures.
        state.failed.length = 0;
    }

    // 1-based position within the batch. Continues from the current total so a
    // second upload merged into a running batch keeps numbering sequential.
    let position = state.uploadCount[1];
    state.uploadCount[1] += files.length;

    if (callback) {
        state.activeCallbacks.add(callback);
    }

    // Rows mirrored into the persisted queue (db target only — the hosting page
    // uploads a site, which is re-deployable, not a user's irreplaceable files).
    const persistRows: PersistedUpload[] = [];

    for (let f of files) {
        position++;
        const relPath = f.webkitRelativePath || f.name;
        const persistId = target === 'db'
            ? persistedUploadId(target, options?.storeKey, currentDir + relPath)
            : undefined;
        state.queue.push({ file: f, currentDir, target, foldersRef: options?.foldersRef, storeKey: options?.storeKey, contentTypeMapping, callback, onItemUploaded: options?.onItemUploaded, onBeforeItem: options?.onBeforeItem, position, relPath, persistId, serviceId, service: uploadService });
        if (persistId && serviceId) {
            persistRows.push({
                id: persistId, serviceId, target: 'db', storeKey: options?.storeKey,
                currentDir, relPath, name: f.name, type: f.type || "",
                size: f.size, lastModified: f.lastModified || 0, file: f, addedAt: Date.now(),
            });
        }
    }

    // Mirror the queue to IndexedDB so a reload, a crash or a closed tab can offer
    // the remainder back instead of losing it. Deliberately NOT awaited: the
    // upload starts now, and a persistence failure must only cost the resume
    // offer. Each row is deleted as its file settles (see settleItem).
    if (persistRows.length) {
        requestPersistentStorage();
        for (const r of persistRows) awaitingMirror.add(r.id);
        void persistPendingUploads(persistRows, {
            stillPending: (row) => awaitingMirror.has(row.id),
        });
    }

    // For the db target, spawn up to DB_CONCURRENCY workers in parallel.
    // The activeWorkers guard inside processUploadQueue prevents excess spawns.
    const workerCount = target === 'db' ? DB_CONCURRENCY : 1;
    for (let i = 0; i < workerCount; i++) {
        processUploadQueue(state);
    }
};

/**
 * Re-enqueue rows recovered from the persisted queue (see listPendingUploads).
 *
 * Separate from uploadFiles because each recovered row carries its OWN
 * currentDir and relPath — a single resume can span folders, which the
 * one-directory-per-call shape of uploadFiles cannot express — and because the
 * rows are already persisted, so re-persisting them would be a no-op write of
 * several thousand records.
 */
export let enqueueResumedUploads = (
    rows: PersistedUpload[],
    options?: {
        foldersRef?: any;
        /** Store the CALLER is showing, used only when a row does not name its
         *  own. `storeKey` is optional on a persisted row, so a row written
         *  before it existed resumes with none, and the folder bookkeeping then
         *  falls back to the hosting subdomain and looks for a db store in the
         *  hosting cache, which can only miss. Rows are read through the
         *  serviceId index, so the caller's store IS the row's store. */
        storeKey?: string;
        callback?: (hasOverwritten: boolean) => void;
        onItemUploaded?: (info: UploadedItemInfo) => void | Promise<void>;
        onBeforeItem?: (info: UploadedItemInfo) => Promise<OverwriteDecision>;
        // No onBeforeBatch here, deliberately. A resume is not a new batch: these
        // rows were queued by an earlier uploadFiles/onDrop call that already
        // asked, and re-asking would interrogate the user again about files they
        // have already answered for, at the one moment (a recovery offer they
        // just accepted) when another modal is least welcome.
    }
) => {
    // Never re-enqueue something this page is still uploading. The offer already
    // subtracts these (see inFlightUploadIds), so reaching here means a stale
    // offer or a second click — and a duplicate item is not a wasted upload, it
    // is a second index request for the same file. Cheap to check, and the check
    // is the only thing standing between a double-click and a doubled document.
    const busy = inFlightUploadIds();
    rows = rows.filter((r) => !busy.has(r.id));
    // A row past the upload phase has no blob: its bytes are already stored and
    // only its indexing is outstanding. Re-uploading is not the repair for that
    // (there is nothing to send), and enqueueing one would push a file with no
    // content through the upload path. Those are repaired by the caller's
    // index-setup pass instead.
    const resumable: Array<{ row: PersistedUpload; file: File }> = [];
    for (const r of rows) {
        const file = rehydratePersistedFile(r);
        if (file) resumable.push({ row: r, file });
    }
    if (!resumable.length) return;
    const target: UploadTarget = 'db';
    const resumeService = currentService.value;
    const liveServiceId = currentService.value?.id || "";
    // Rows are read through the serviceId index, so in practice they all belong
    // to the project showing this offer — but each row still goes on ITS OWN
    // project's queue rather than a shared one, so a row written under a
    // different project can never be resumed into this one's batch.
    const states = new Set<TargetState>();

    for (const { row: r, file } of resumable) {
        const state = dbUploadStateFor(r.serviceId || liveServiceId);
        if (!states.has(state)) {
            states.add(state);
            if (state.activeWorkers === 0) state.failed.length = 0;
            if (options?.callback) state.activeCallbacks.add(options.callback);
        }
        const position = ++state.uploadCount[1];
        state.queue.push({
            file,
            currentDir: r.currentDir,
            target,
            foldersRef: options?.foldersRef,
            storeKey: r.storeKey || options?.storeKey,
            contentTypeMapping,
            callback: options?.callback,
            onItemUploaded: options?.onItemUploaded,
            onBeforeItem: options?.onBeforeItem,
            position,
            relPath: r.relPath,
            persistId: r.id,
            serviceId: r.serviceId,
            // Rows are read through the serviceId index, so the project showing
            // this offer IS the project these files belong to.
            service: resumeService,
        });
    }

    for (const state of states) {
        for (let i = 0; i < DB_CONCURRENCY; i++) {
            processUploadQueue(state);
        }
    }
};

let contentTypeMapping: { [filename: string]: string } = {};

function traverseFileTree(item: { [key: string]: any }, path = "") {
    return new Promise((resolve) => {
        if (item.isFile) {
            item.file(function (file: { [key: string]: any }) {
                if (!file.type) {
                    function getExtensionOfFilename(filename: string) {
                        var _fileLen = filename.length;
                        var _lastDot = filename.lastIndexOf(".");
                        var _fileExt = filename
                            .substring(_lastDot, _fileLen)
                            .toLowerCase();
                        return _fileExt;
                    }
                    let type = getExtensionOfFilename(file.name);
                    contentTypeMapping[file.name] = mime.getType(type);
                }
                resolve([{ file, path: path + file.name }]);
            }, () => resolve([]));
        } else if (item.isDirectory) {
            // readEntries hands back the directory in CHUNKS (Chromium caps a
            // single call at 100 entries) and signals the end with an empty
            // array, so it must be called repeatedly. Calling it once silently
            // truncated every dropped folder to its first 100 files per
            // directory: the upload reported success having skipped the rest.
            // agent.vue's readEntry and the widget's copy both loop; this one
            // did not.
            let dirReader = item.createReader();
            let all: any[] = [];
            let readBatch = () => {
                dirReader.readEntries(async function (
                    entries: { [key: string]: any }[]
                ) {
                    if (!entries.length) {
                        resolve(all);
                        return;
                    }
                    let promises = entries.map((entry) =>
                        traverseFileTree(entry, path + item.name + "/")
                    );
                    let results = await Promise.all(promises);
                    // Appended with a loop rather than push(...group)/concat: a
                    // group is a whole subtree, and spreading a deep one blows
                    // the argument limit.
                    for (let group of results) {
                        for (let entry of group as any[]) all.push(entry);
                    }
                    readBatch();
                }, () => resolve(all));
            };
            readBatch();
        } else {
            // Neither file nor directory. Leaving the promise pending here hung
            // the Promise.all in onDrop, so the whole drop silently did nothing.
            resolve([]);
        }
    });
}

export let onDrop = async (
    e: any,
    callback?: (hasOverwritten: boolean) => void,
    options?: {
        target?: 'db' | 'host';
        currentDir?: string;
        foldersRef?: any;
        storeKey?: string;
        onItemUploaded?: (info: UploadedItemInfo) => void | Promise<void>;
        onBeforeItem?: (info: UploadedItemInfo) => Promise<OverwriteDecision>;
        /** Asked ONCE for the whole drop, after the directory walk resolves and
         *  before the first file is queued. See BeforeBatchHook. */
        onBeforeBatch?: BeforeBatchHook;
    }
) => {
    e.preventDefault();

    let items = e.dataTransfer.items;
    let formData = new FormData();
    let allFiles = [];
    let filePromises = [];

    for (let i = 0; i < items.length; i++) {
        let item = items[i].webkitGetAsEntry();

        if (item) {
            filePromises.push(traverseFileTree(item));
        }
    }

    let allFileGroups = await Promise.all(filePromises);
    allFiles = [].concat(...allFileGroups);

    allFiles.forEach(({ file, path }) => {
        formData.append("files[]", file, path);
    });

    const dropped = formData.getAll("files[]") as File[];

    // The drop asks HERE rather than leaving it to uploadFiles, because the walk
    // above is the only thing that knows what a dropped FOLDER contains and it
    // has to finish before there is a list to ask about. That is the whole
    // ordering the feature rests on: one question, asked once we know what was
    // dropped, before a byte moves. A cancel returns without calling uploadFiles
    // at all, so nothing is queued and no counter ever moved.
    if (options?.onBeforeBatch) {
        const proceed = await runBeforeBatch(
            options.onBeforeBatch,
            {
                files: [...dropped],
                target: options?.target === 'db' ? 'db' : 'host',
                currentDir: resolveUploadDir(options?.currentDir),
                storeKey: options?.storeKey,
                serviceId: currentService.value?.id || "",
            },
            callback,
        );
        if (!proceed) return;
        // Answered: hand the rest over with the hook REMOVED, or uploadFiles
        // would ask the very same question about the very same files a second
        // time. Note this branch keeps uploadFiles un-awaited, exactly like the
        // no-hook one below, so the returned promise still settles when the
        // files are queued (which is when the progress rows can replace the
        // caller's "preparing" scrim).
        uploadFiles(dropped, callback, contentTypeMapping, { ...options, onBeforeBatch: undefined });
        return;
    }

    uploadFiles(
        dropped,
        callback,
        contentTypeMapping,
        options
    );
};
